// Dashboard Mesyuarat (Prompt F) — paparan mesyuarat PIBG/lembaga:
// KPI, carta bar kutipan vs tunggakan per kelas, leaderboard % kutipan + wali kelas,
// donut status, trend 12 bulan, top-10 tunggakan, mod pembentangan, cetak.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, Presentation, X, Loader2, Trophy, TrendingUp, Users, Banknote, AlertTriangle, Percent } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import { supabase } from '../../../lib/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import { formatRM, formatCompact } from '../../../lib/format';
import { paidByInvoice, invoiceOutstanding } from '../../../lib/fees';
import { printLaporan } from '../../../lib/laporan';
import { CardSkeleton } from '../../../components/Skeletons';
import { Button } from '../../../components/ui/button';

const GREEN = '#059669', RED = '#dc2626', BLUE = '#1E3A8A', GOLD = '#F59E0B';
const DONUT = { lunas: GREEN, separa: GOLD, belum: RED };

export default function Mesyuarat() {
  const { t } = useTranslation();
  const { school } = useAuth();
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [years, setYears] = useState([]);
  const [present, setPresent] = useState(false);
  const [data, setData] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [{ data: students }, { data: classes }, { data: invoices }, { data: pays }, { data: profiles }] = await Promise.all([
        supabase.from('students').select('id, nama, class_id').eq('aktif', true),
        supabase.from('classes').select('id, nama, wali_kelas_id'),
        supabase.from('invoices').select('id, student_id, jumlah_bayar_rm, status, periode, due_date'),
        supabase.from('payments').select('invoice_id, student_id, jumlah_rm, tanggal').eq('voided', false),
        supabase.from('profiles').select('id, nama'),
      ]);
      const yrs = [...new Set((invoices || []).map((i) => (i.periode || '').slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))].sort().reverse();
      setYears(yrs);
      setYear((prev) => (yrs.length && !yrs.includes(prev) ? yrs[0] : prev));
      setData({ students: students || [], classes: classes || [], invoices: invoices || [], pays: pays || [], profiles: profiles || [] });
      setLoading(false);
    })();
  }, []);

  const stats = useMemo(() => {
    if (!data) return null;
    const { students, classes, invoices, pays, profiles } = data;
    const paidMap = paidByInvoice(pays);
    const yInv = invoices.filter((i) => (i.periode || '').startsWith(year));
    const yInvIds = new Set(yInv.map((i) => i.id));
    const yPays = pays.filter((p) => yInvIds.has(p.invoice_id));
    const billed = yInv.reduce((a, i) => a + Number(i.jumlah_bayar_rm || 0), 0);
    const collected = yPays.reduce((a, p) => a + Number(p.jumlah_rm || 0), 0);
    const arrears = yInv.reduce((a, i) => a + invoiceOutstanding(i, paidMap), 0);
    const pct = billed > 0 ? Math.round((collected / billed) * 100) : 0;

    const cMap = Object.fromEntries(classes.map((c) => [c.id, c]));
    const pMap = Object.fromEntries(profiles.map((p) => [p.id, p.nama]));
    const sClass = Object.fromEntries(students.map((s) => [s.id, s.class_id]));

    // Per kelas
    const perKelas = classes.map((c) => {
      const invs = yInv.filter((i) => sClass[i.student_id] === c.id);
      const bil = invs.reduce((a, i) => a + Number(i.jumlah_bayar_rm || 0), 0);
      const kut = invs.reduce((a, i) => a + (paidMap[i.id] || 0), 0);
      const tung = invs.reduce((a, i) => a + invoiceOutstanding(i, paidMap), 0);
      return {
        nama: c.nama, wali: pMap[c.wali_kelas_id] || '-',
        kutipan: Math.round(kut), tunggakan: Math.round(tung),
        pct: bil > 0 ? Math.round((kut / bil) * 100) : 0,
        murid: students.filter((s) => s.class_id === c.id).length,
      };
    }).filter((k) => k.kutipan > 0 || k.tunggakan > 0);
    const leaderboard = [...perKelas].sort((a, b) => b.pct - a.pct);

    // Donut status (amaun)
    const donut = ['lunas', 'separa', 'belum'].map((s) => ({
      name: s, value: Math.round(yInv.filter((i) => i.status === s).reduce((a, i) => a + Number(i.jumlah_bayar_rm || 0), 0)),
    })).filter((d) => d.value > 0);

    // Trend 12 bulan
    const trend = [];
    const now = new Date();
    for (let k = 11; k >= 0; k--) {
      const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('ms-MY', { month: 'short' });
      const amt = pays.filter((p) => (p.tanggal || '').startsWith(key)).reduce((a, p) => a + Number(p.jumlah_rm || 0), 0);
      trend.push({ bulan: label, kutipan: Math.round(amt) });
    }

    // Top 10 tunggakan
    const invByS = {};
    yInv.forEach((i) => { (invByS[i.student_id] = invByS[i.student_id] || []).push(i); });
    const top = students.map((s) => {
      const tung = (invByS[s.id] || []).reduce((a, i) => a + invoiceOutstanding(i, paidMap), 0);
      return { nama: s.nama, kelas: cMap[s.class_id]?.nama || '-', tunggakan: Math.round(tung) };
    }).filter((x) => x.tunggakan > 0).sort((a, b) => b.tunggakan - a.tunggakan).slice(0, 10);

    return { billed, collected, arrears, pct, perKelas, leaderboard, donut, trend, top, murid: students.length };
  }, [data, year]);

  const togglePresent = () => {
    if (!present) wrapRef.current?.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
    setPresent(!present);
  };

  if (loading) return <div className="p-6"><CardSkeleton count={4} /></div>;

  const kpi = [
    { icon: Banknote, label: t('kutipanTahun'), value: formatRM(stats.collected), color: 'text-emerald-600 dark:text-emerald-400' },
    { icon: AlertTriangle, label: t('tunggakanSemasa'), value: formatRM(stats.arrears), color: 'text-red-600 dark:text-red-400' },
    { icon: Percent, label: t('kadarKutipan'), value: `${stats.pct}%`, color: 'text-brand' },
    { icon: Users, label: t('jumlahMurid'), value: stats.murid, color: 'text-foreground' },
  ];

  return (
    <div ref={wrapRef} className={present ? 'bg-slate-950 min-h-screen p-8 overflow-y-auto' : ''}>
      <div className={`flex flex-wrap items-center gap-3 mb-6 ${present ? 'text-white' : ''}`}>
        <div className="print:hidden">
          <h1 className={`text-xl sm:text-2xl font-black ${present ? 'text-white text-4xl' : 'text-foreground'}`}>{t('dashboardMesyuarat')}</h1>
          <p className={`text-sm ${present ? 'text-muted-foreground text-lg' : 'text-muted-foreground'}`}>{school?.nama} · {t('tahun')}: {year}</p>
        </div>
        <div className="hidden print:block">
          <h1 className="text-2xl font-black">{t('dashboardMesyuarat')} — {school?.nama}</h1>
          <p className="text-sm text-muted-foreground">{t('tahun')}: {year}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 print:hidden">
          <select value={year} onChange={(e) => setYear(e.target.value)}
            className="h-9 sm:h-10 px-2 sm:px-3 rounded-xl border border-border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand bg-card text-foreground">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button onClick={togglePresent} className="h-9 sm:h-10 text-sm bg-brand text-white hover:bg-brand-royal">
            {present ? <X className="h-4 w-4 mr-1.5" /> : <Presentation className="h-4 w-4 mr-1.5" />}
            {present ? t('keluarPresent') : t('modPresent')}
          </Button>
          <Button variant="outline" onClick={() => printLaporan(t('dashboardMesyuarat'))} className={`h-9 sm:h-10 text-sm ${present ? 'bg-card' : ''}`}>
            <Printer className="h-4 w-4 mr-1.5" />{t('cetak')}
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 ${present ? 'gap-6' : ''}`}>
        {kpi.map((k) => (
          <div key={k.label} className={`rounded-2xl border p-4 sm:p-5 min-w-0 ${present ? 'bg-slate-900 border-slate-700 dark:bg-black/50 dark:border-slate-800 p-8' : 'bg-card border-border'}`}>
            <div className="flex items-center gap-2 min-w-0">
              <k.icon className={`h-5 w-5 shrink-0 ${present ? 'text-gold' : k.color}`} />
              <p className={`uppercase tracking-wider font-semibold leading-tight ${present ? 'text-muted-foreground text-sm' : 'text-[11px] sm:text-xs text-muted-foreground'}`}>{k.label}</p>
            </div>
            <p className={`mt-2 font-black font-mono tabular whitespace-nowrap ${present ? 'text-5xl text-white' : `text-xl sm:text-3xl ${k.color}`}`}>{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Bar per kelas */}
        <div className={`rounded-2xl border p-5 ${present ? 'bg-slate-900 border-slate-700 dark:bg-black/50 dark:border-slate-800' : 'bg-card border-border'}`}>
          <h2 className={`font-bold mb-4 ${present ? 'text-white text-2xl' : 'text-foreground'}`}>{t('kutipanVsTunggakan')}</h2>
          <ResponsiveContainer width="100%" height={present ? 380 : 280}>
            <BarChart data={stats.perKelas} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={present ? '#334155' : 'hsl(var(--border))'} />
              <XAxis type="number" tickFormatter={(v) => formatCompact(v)} tick={{ fill: present ? '#cbd5e1' : 'hsl(var(--muted-foreground))', fontSize: 12 }} />
              <YAxis type="category" dataKey="nama" width={90} tick={{ fill: present ? '#cbd5e1' : 'hsl(var(--muted-foreground))', fontSize: 12 }} />
              <Tooltip formatter={(v) => formatRM(v)} contentStyle={{ borderRadius: 12, backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--card-foreground))' }} />
              <Legend />
              <Bar dataKey="kutipan" name={t('kutipan')} fill={GREEN} radius={[0, 6, 6, 0]} />
              <Bar dataKey="tunggakan" name={t('tunggakan')} fill={RED} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Donut status */}
        <div className={`rounded-2xl border p-5 ${present ? 'bg-slate-900 border-slate-700 dark:bg-black/50 dark:border-slate-800' : 'bg-card border-border'}`}>
          <h2 className={`font-bold mb-4 ${present ? 'text-white text-2xl' : 'text-foreground'}`}>{t('statusBil')}</h2>
          <ResponsiveContainer width="100%" height={present ? 380 : 280}>
            <PieChart>
              <Pie data={stats.donut} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={3}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {stats.donut.map((d) => <Cell key={d.name} fill={DONUT[d.name]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatRM(v)} contentStyle={{ borderRadius: 12, backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--card-foreground))' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Trend 12 bulan */}
      <div className={`rounded-2xl border p-5 mb-6 ${present ? 'bg-slate-900 border-slate-700 dark:bg-black/50 dark:border-slate-800' : 'bg-card border-border'}`}>
        <h2 className={`font-bold mb-4 flex items-center gap-2 ${present ? 'text-white text-2xl' : 'text-foreground'}`}>
          <TrendingUp className="h-5 w-5" />{t('trend12Bulan')}
        </h2>
        <ResponsiveContainer width="100%" height={present ? 320 : 220}>
          <AreaChart data={stats.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke={present ? '#334155' : 'hsl(var(--border))'} />
            <XAxis dataKey="bulan" tick={{ fill: present ? '#cbd5e1' : 'hsl(var(--muted-foreground))', fontSize: 12 }} />
            <YAxis tickFormatter={(v) => formatCompact(v)} tick={{ fill: present ? '#cbd5e1' : 'hsl(var(--muted-foreground))', fontSize: 12 }} />
            <Tooltip formatter={(v) => formatRM(v)} contentStyle={{ borderRadius: 12, backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--card-foreground))' }} />
            <Area type="monotone" dataKey="kutipan" name={t('kutipan')} stroke={BLUE} fill={BLUE} fillOpacity={present ? 0.4 : 0.15} strokeWidth={3} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Leaderboard */}
        <div className={`rounded-2xl border p-5 ${present ? 'bg-slate-900 border-slate-700 dark:bg-black/50 dark:border-slate-800' : 'bg-card border-border'}`}>
          <h2 className={`font-bold mb-4 flex items-center gap-2 ${present ? 'text-white text-2xl' : 'text-foreground'}`}>
            <Trophy className="h-5 w-5 text-gold" />{t('leaderboardKelas')}
          </h2>
          <div className="space-y-3">
            {stats.leaderboard.map((k, i) => (
              <div key={k.nama}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className={`font-semibold ${present ? 'text-white text-base' : 'text-foreground'}`}>
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-black mr-2 ${i === 0 ? 'bg-gold text-white' : present ? 'bg-slate-700 text-slate-200' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                    {k.nama} <span className={`font-normal ${present ? 'text-muted-foreground' : 'text-muted-foreground'}`}>· {k.wali}</span>
                  </span>
                  <span className={`font-mono font-black tabular ${present ? 'text-white text-lg' : k.pct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : k.pct >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>{k.pct}%</span>
                </div>
                <div className={`h-2.5 rounded-full overflow-hidden ${present ? 'bg-slate-700' : 'bg-muted'}`}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${k.pct}%`, background: k.pct >= 80 ? GREEN : k.pct >= 50 ? GOLD : RED }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 10 tunggakan */}
        <div className={`rounded-2xl border p-5 ${present ? 'bg-slate-900 border-slate-700 dark:bg-black/50 dark:border-slate-800' : 'bg-card border-border'}`}>
          <h2 className={`font-bold mb-4 ${present ? 'text-white text-2xl' : 'text-foreground'}`}>{t('top10Tunggakan')}</h2>
          <div className="space-y-2">
            {stats.top.map((s, i) => (
              <div key={i} className={`flex items-center justify-between py-2 border-b last:border-0 ${present ? 'border-slate-700' : 'border-slate-50'}`}>
                <span className={`text-sm ${present ? 'text-slate-200 text-base' : 'text-foreground'}`}>
                  <span className={`font-mono font-bold mr-2 ${present ? 'text-muted-foreground' : 'text-muted-foreground'}`}>{i + 1}.</span>
                  {s.nama} <span className={present ? 'text-muted-foreground' : 'text-muted-foreground'}>· {s.kelas}</span>
                </span>
                <span className={`font-mono font-bold tabular ${present ? 'text-red-400 text-lg' : 'text-red-600 dark:text-red-400'}`}>{formatRM(s.tunggakan)}</span>
              </div>
            ))}
            {stats.top.length === 0 && <p className={`text-sm ${present ? 'text-muted-foreground' : 'text-muted-foreground'}`}>{t('tiadaTunggakan')}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
